"use client";

import React from "react";
import { motion } from "motion/react";

/**
 * SlideUpIn Animation Component
 *
 * A simpler version of SlideUpInOut that only runs once when
 * the element enters the viewport.
 *
 * - Only animates from bottom to top (Slide Up)
 * - Persists after first animation (no exit animation)
 * - Optimized for standard list/grid entries
 */

interface SlideUpInProps {
  /**
   * Content to be animated
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes to apply to the container
   */
  className?: string;
  /**
   * Animation offset in pixels (starts from this Y value)
   */
  offset?: number;
  /**
   * Animation duration in seconds
   */
  duration?: number;
  /**
   * Animation delay in seconds
   */
  delay?: number;
  /**
   * Threshold for when to start the animation (0 to 1)
   */
  threshold?: number;
}

export const SlideUpIn: React.FC<SlideUpInProps> = ({
  children,
  className,
  offset = 40,
  duration = 0.5,
  delay = 0,
  threshold = 0.1,
}) => {
  return (
    <motion.div
      initial={{
        y: offset,
        opacity: 0,
      }}
      whileInView={{
        y: 0,
        opacity: 1,
      }}
      viewport={{
        once: true,
        amount: threshold,
      }}
      transition={{
        duration,
        delay,
        ease: [0.21, 0.47, 0.32, 0.98], // Decelerating ease
      }}
      className={className}
      style={{ willChange: "transform, opacity" }}
    >
      {children}
    </motion.div>
  );
};
